/**
 * 调查收藏分组 — 每个用户独立的收藏标签（如「待打样」「客户已看」）。
 * 两个组件：
 *   FavGroupBar    收藏视图下的分组筛选条（含新建 / 重命名 / 删除）
 *   FavGroupPicker 单张卡片上的分组勾选气泡
 */
import { useState } from "react";
import { Tags, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function FavGroupBar({ groups, meta, activeGroup, onPick, onChanged }) {
  const { t } = useLanguage();
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);

  const create = async (raw) => {
    const v = (raw || "").trim();
    if (!v) { setAdding(false); return; }
    try {
      await api.post("/market/fav-groups", { name: v });
      setAdding(false); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const rename = async (gid, v) => {
    if (!v.trim()) return;
    try { await api.patch(`/market/fav-groups/${gid}`, { name: v.trim() }); setEditId(null); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (gid) => {
    try {
      await api.delete(`/market/fav-groups/${gid}`);
      if (activeGroup === gid) onPick("");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1.5"
      data-testid="fav-group-bar">
      <Tags size={13} className="text-amber-600 shrink-0" />
      <Chip active={!activeGroup} onClick={() => onPick("")} testId="fav-group-all">
        {t("market.favAll")} {meta?.total ? `(${meta.total})` : ""}
      </Chip>
      {groups.map((g) => (
        editId === g.id ? (
          <InlineEdit key={g.id} initial={g.name} onCancel={() => setEditId(null)}
            onSave={(v) => rename(g.id, v)} testId={`fav-group-edit-${g.id}`} />
        ) : (
          <span key={g.id} className="group inline-flex items-center">
            <Chip active={activeGroup === g.id} onClick={() => onPick(g.id)} testId={`fav-group-${g.id}`}>
              {g.name} ({g.count})
            </Chip>
            <button type="button" onClick={() => setEditId(g.id)} title={t("common.edit")}
              className="ml-0.5 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-brand transition-opacity"
              data-testid={`fav-group-rename-${g.id}`}><Pencil size={11} /></button>
            <button type="button" onClick={() => remove(g.id)} title={t("common.delete")}
              className="ml-0.5 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
              data-testid={`fav-group-delete-${g.id}`}><Trash2 size={11} /></button>
          </span>
        )
      ))}
      {meta?.ungrouped ? (
        <Chip active={activeGroup === "__none__"} onClick={() => onPick("__none__")} testId="fav-group-none">
          {t("market.favUngrouped")} ({meta.ungrouped})
        </Chip>
      ) : null}

      {adding ? (
        <InlineEdit initial="" onCancel={() => setAdding(false)}
          onSave={create} testId="fav-group-new-input" />
      ) : (
        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px] text-amber-700"
          onClick={() => setAdding(true)} data-testid="fav-group-add">
          <Plus size={12} /> {t("market.favNewGroup")}
        </Button>
      )}
    </div>
  );
}

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`h-6 px-2 rounded-full text-[11px] font-medium transition-colors ${
      active ? "bg-amber-500 text-white" : "bg-white border border-amber-200 text-amber-800 hover:bg-amber-100"}`}>
    {children}
  </button>
);

const InlineEdit = ({ initial, onSave, onCancel, testId }) => {
  const [v, setV] = useState(initial);
  return (
    <span className="inline-flex items-center gap-1">
      <Input autoFocus value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") onSave(v); if (e.key === "Escape") onCancel(); }}
        className="h-6 w-28 text-[11px] px-1.5" data-testid={testId} />
      <button type="button" onClick={() => onSave(v)} className="text-emerald-600"><Check size={12} /></button>
      <button type="button" onClick={onCancel} className="text-muted-foreground"><X size={12} /></button>
    </span>
  );
};

export function FavGroupPicker({ product, groups, onSaved }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = product.fav_group_ids || [];

  const toggle = async (gid) => {
    const next = current.includes(gid) ? current.filter((g) => g !== gid) : [...current, gid];
    setBusy(true);
    try {
      await api.put(`/market/products/${product.id}/favorite/groups`, { group_ids: next });
      onSaved(product.id, next);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()}
          title={t("market.favGroups")}
          className="absolute top-1.5 right-8 z-20 rounded p-0.5 bg-background/80 backdrop-blur shadow-sm"
          data-testid={`market-product-fav-groups-${product.id}`}>
          <Tags size={14} className={current.length ? "text-amber-500" : "text-muted-foreground"} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48 p-2" onClick={(e) => e.stopPropagation()}>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{t("market.favGroups")}</p>
        {groups.length === 0 && (
          <p className="text-[11px] text-muted-foreground">{t("market.favNoGroups")}</p>
        )}
        <div className="space-y-0.5">
          {groups.map((g) => (
            <button key={g.id} type="button" disabled={busy} onClick={() => toggle(g.id)}
              className="w-full flex items-center gap-1.5 rounded px-1.5 py-1 text-xs hover:bg-secondary/60 text-left"
              data-testid={`fav-assign-${g.id}-${product.id}`}>
              <span className={`h-3 w-3 rounded-sm border flex items-center justify-center ${
                current.includes(g.id) ? "bg-amber-500 border-amber-500" : "border-border"}`}>
                {current.includes(g.id) && <Check size={9} className="text-white" />}
              </span>
              <span className="truncate">{g.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
